import { TitleCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatOption } from '@angular/material/core';
import { MatError, MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { environment } from '@environments/environments';
import { OfficeTenantsService } from '@office/tenants/office-tenants.service';
import { BankAccountDrawerBaseComponent } from '@shared/components/page-layout/drawers/bank-account-drawer-base.component';
import { US_STATES } from '@shared/constants/us-states.constants';
import { AdditionalCoverageSlugs } from '@shared/constants/website-settings-slugs.type';
import { BankAccountOwnershipType, BankAccountType } from '@shared/enums/bank-account.enum';
import { enumToDescriptedArray } from '@shared/functions/utils.type';
import { BraintreeBankAccountError, BraintreeBankAccountFormValue, BraintreeUsBankAccountTokenizePayload, BraintreeUsBankAccountTokenizer, BraintreeUsBankDetails } from '@shared/models/domain/custom-views.model';
import { WebsiteSettingsView } from '@shared/models/domain/domain-view.model';
import { SelectOption } from '@shared/models/shared/select-option.model';
import { WebsiteSettingsViewService } from '@shared/services/settings.service';
import type { Client } from 'braintree-web';
import braintree from 'braintree-web';
import { NgxMaskDirective } from 'ngx-mask';
import { takeUntil } from 'rxjs';

@Component({
    selector: 'app-braintree-bank-account-drawer',
    templateUrl: './braintree-bank-account-drawer.component.html',
    styleUrls: ['./braintree-bank-account-drawer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatIcon, FormsModule, ReactiveFormsModule, MatCheckbox, MatFormField, MatLabel, MatSelect, MatOption, MatInput, MatButton, NgxMaskDirective, MatError, MatHint, TitleCasePipe]
})
export class BraintreeBankAccountDrawerComponent extends BankAccountDrawerBaseComponent {
    private websiteSettingsViewService = inject(WebsiteSettingsViewService);
    private officeTenantsService = inject(OfficeTenantsService);

    usBankAccount: BraintreeUsBankAccountTokenizer;
    bankAccountOwnershipType = BankAccountOwnershipType;
    bankAccountType = BankAccountType;
    options: Record<string, SelectOption[]> = {
        accountType: enumToDescriptedArray(BankAccountType),
        ownershipType: enumToDescriptedArray(BankAccountOwnershipType),
        states: US_STATES
    };

    private websiteSettings: Record<string, WebsiteSettingsView>;

    protected setupGatewayFormControls(isEditMode: boolean): void {
        this.bankAccountForm.addControl('ownershipType', this.formBuilder.control(BankAccountOwnershipType.Personal, [Validators.required]));
        this.bankAccountForm.addControl('accountType', this.formBuilder.control(BankAccountType.Checking, [Validators.required]));
        this.bankAccountForm.addControl('accountHolderName', this.formBuilder.control('', [Validators.required]));
        this.bankAccountForm.addControl('routingNumber', this.formBuilder.control('', [Validators.required, Validators.minLength(9)]));
        this.bankAccountForm.addControl('accountNumber', this.formBuilder.control('', [Validators.required, Validators.minLength(4), Validators.maxLength(17)]));
        this.bankAccountForm.addControl('firstName', this.formBuilder.control('', [Validators.required]));
        this.bankAccountForm.addControl('lastName', this.formBuilder.control('', [Validators.required]));
        this.bankAccountForm.addControl('businessName', this.formBuilder.control(''));
        this.bankAccountForm.addControl('billingStreetAddress', this.formBuilder.control('', [Validators.required]));
        this.bankAccountForm.addControl('billingExtendedAddress', this.formBuilder.control(''));
        this.bankAccountForm.addControl('billingLocality', this.formBuilder.control('', [Validators.required]));
        this.bankAccountForm.addControl('billingRegion', this.formBuilder.control('', [Validators.required]));
        this.bankAccountForm.addControl('billingPostalCode', this.formBuilder.control('', [Validators.required]));

        this.websiteSettingsViewService.currentWebsiteSettings$
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe((settings) => this.websiteSettings = settings);

        if (isEditMode) {
            return;
        }

        this.prefillUserInformation();

        this.bankAccountControls.ownershipType?.valueChanges
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe((ownershipType: BankAccountOwnershipType) => {
                this.updateOwnershipValidation(ownershipType);
            });
    }

    protected initializeGateway(token: string): void {
        braintree.client.create({
            authorization: token
        }, this.clientInstance.bind(this) as typeof this.clientInstance);
    }

    clientInstance(err, clientInstance: Client): void {
        if (err) {
            this.busy = false;
            this.changeDetectorRef.detectChanges();
            this.alertService.errorToast('Connection Error', 'Failed to initialize secure payment connection. Please refresh the page and try again.');
            return;
        }

        braintree.usBankAccount.create({
            client: clientInstance
        }, (usBankAccountError, usBankAccountInstance) => {
            if (usBankAccountError) {
                this.busy = false;
                this.changeDetectorRef.detectChanges();
                this.handleError(usBankAccountError);
                return;
            }

            this.usBankAccount = usBankAccountInstance;
            this.busy = false;
            this.changeDetectorRef.detectChanges();
        });
    }

    protected submitNewCard(): void {
        if (!this.bankAccountForm.valid || !this.usBankAccount) {
            this.alertService.errorToast('Error', 'Please fill in all required fields.');
            return;
        }

        this.busy = true;
        this.changeDetectorRef.detectChanges();
        const formValues = this.bankAccountForm.value as BraintreeBankAccountFormValue;

        // Prepare bank details for Braintree tokenization
        const bankDetails: BraintreeUsBankDetails = {
            accountNumber: formValues.accountNumber,
            routingNumber: formValues.routingNumber,
            accountType: BankAccountType[formValues.accountType]?.toLocaleLowerCase(),
            ownershipType: BankAccountOwnershipType[formValues.ownershipType]?.toLocaleLowerCase(),
            billingAddress: {
                streetAddress: formValues.billingStreetAddress,
                extendedAddress: formValues.billingExtendedAddress || '',
                locality: formValues.billingLocality,
                region: formValues.billingRegion,
                postalCode: formValues.billingPostalCode,
                countryCodeAlpha2: 'US'
            }
        };

        // Add name fields based on ownership type
        if (formValues.ownershipType === BankAccountOwnershipType.Personal) {
            bankDetails['firstName'] = formValues.firstName;
            bankDetails['lastName'] = formValues.lastName;
        } else if (formValues.ownershipType === BankAccountOwnershipType.Business) {
            bankDetails['businessName'] = formValues.businessName;
        }

        const titleCasePipe = new TitleCasePipe();
        let mandateText = '';
        const userBankAccountAgreement = this.websiteSettings[AdditionalCoverageSlugs.userBankAccountAgreement];
        if (userBankAccountAgreement?.settingsValue) {
            mandateText = userBankAccountAgreement.settingsValue;
            const websiteName = titleCasePipe.transform(this.officeTenantsService.getTenantDetailsBySlug()?.slug ?? environment.defaultWebsiteSlug);
            const tokens = {
                '${websiteName}': websiteName,
                '${accountHolderName}': formValues.accountHolderName,
                '${insertAccountType}': BankAccountType[formValues.accountType],
                '${insertAccountNumber}': formValues.accountNumber.slice(-4),
                '${insertRoutingNumber}': formValues.routingNumber,
                '${insertDate}': new Date().toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                })
            };

            Object.entries(tokens).forEach(([key, value]) => {
                mandateText = mandateText.replaceAll(key, String(value));
            });
        }

        // Tokenize with bank details
        this.usBankAccount.tokenize({
            bankDetails: bankDetails,
            mandateText: mandateText
        }, this.onTokenize.bind(this) as typeof this.onTokenize);
    }

    onTokenize(err: BraintreeBankAccountError, payload?: BraintreeUsBankAccountTokenizePayload): void {
        if (err || !payload?.nonce) {
            this.handleError(err);
            return;
        }
        this.persistNewBankAccount({
            externalBankAccountIdNonce: payload.nonce,
            accountType: this.bankAccountControls.accountType.value as BankAccountType,
            ownershipType: this.bankAccountControls.ownershipType.value as BankAccountOwnershipType
        });
    }

    private handleError(e: BraintreeBankAccountError): void {
        this.alertService.errorToast('Error', e?.details?.originalError?.details?.originalError[0]?.message || e?.error?.ExceptionMessage || e?.message || e?.ExceptionMessage
            || 'Failed to process bank account information. Please try again.');
        this.busy = false;
        this.changeDetectorRef.detectChanges();
    }

    /**
     * Update validation based on ownership type
     */
    private updateOwnershipValidation(ownershipType: BankAccountOwnershipType): void {
        const firstNameControl = this.bankAccountControls.firstName;
        const lastNameControl = this.bankAccountControls.lastName;
        const businessNameControl = this.bankAccountControls.businessName;

        if (ownershipType === BankAccountOwnershipType.Personal) {
            firstNameControl?.setValidators([Validators.required]);
            lastNameControl?.setValidators([Validators.required]);
            businessNameControl?.clearValidators();
        } else if (ownershipType === BankAccountOwnershipType.Business) {
            firstNameControl?.clearValidators();
            lastNameControl?.clearValidators();
            businessNameControl?.setValidators([Validators.required]);
        }

        firstNameControl?.updateValueAndValidity();
        lastNameControl?.updateValueAndValidity();
        businessNameControl?.updateValueAndValidity();
    }

    /**
     * Pre-fill user information based on current user or managed user
     */
    private prefillUserInformation(): void {
        const user = this.isCustomer
            ? this.authService.userProfile?.userProfile
            : this.drawer?.data?.userData;

        this.bankAccountForm.patchValue({
            firstName: user?.firstName || '',
            lastName: user?.lastName || ''
        });
    }
}
